import { useEffect, useState } from 'react';
import { databaseBackupAPI } from '../../services/api';

function formatBytes(bytes) {
  if (!bytes) return '0 KB';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

export default function DatabaseBackups() {
  const [backups, setBackups] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  function load() {
    setLoading(true);
    databaseBackupAPI.list()
      .then((res) => {
        setBackups(res.backups || []);
        setEmail(res.notification_email || '');
      })
      .catch(() => setError('Failed to load backups.'))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleSaveEmail(e) {
    e.preventDefault();
    setSavingEmail(true);
    setError('');
    setMessage('');
    try {
      await databaseBackupAPI.updateSettings(email);
      setMessage('Notification email saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save email.');
    } finally {
      setSavingEmail(false);
    }
  }

  async function handleRunNow() {
    setRunning(true);
    setError('');
    setMessage('');
    try {
      await databaseBackupAPI.run();
      setMessage('Backup queued — the background worker runs it within a minute. Checking…');
      pollForCompletion();
    } catch (err) {
      setError(err.response?.data?.error || 'Backup failed.');
      setRunning(false);
    }
  }

  function pollForCompletion(attempt = 0) {
    databaseBackupAPI.lastRun()
      .then((res) => {
        if (!res.pending) {
          const r = res.last_result;
          if (r?.success) {
            setMessage(`Backup created: ${r.file} (${formatBytes(r.size_bytes)}). ${r.email_note}`);
          } else if (r) {
            setError(r.error || 'Backup failed.');
          }
          setRunning(false);
          load();
          return;
        }
        if (attempt < 20) {
          setTimeout(() => pollForCompletion(attempt + 1), 3000);
        } else {
          setMessage('Still running — check back shortly, or refresh this page.');
          setRunning(false);
        }
      })
      .catch(() => setRunning(false));
  }

  async function handleDownload(name) {
    try {
      const res = await databaseBackupAPI.download(name);
      const url = URL.createObjectURL(res);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed.');
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Database Backups</h1>
        <p className="mt-1 text-sm text-slate-500">
          Runs automatically once a day. Database only (no uploaded files), compressed, and emailed offsite if an
          address is configured below. The last 14 backups are kept on the server.
        </p>
      </div>

      {error && <p className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">{error}</p>}
      {message && <p className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700">{message}</p>}

      <form onSubmit={handleSaveEmail} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
        <label className="block text-sm font-medium text-slate-700">Offsite notification email</label>
        <div className="flex flex-wrap gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@awtf.or.tz"
            className="min-w-64 flex-1 rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <button type="submit" disabled={savingEmail}
            className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2.5 text-sm font-semibold">
            {savingEmail ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Each backup is emailed here as a compressed attachment. Leave blank to keep backups on the server only
          (not recommended — that offers no protection if this server itself has a problem).
        </p>
      </form>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Run a backup now</h2>
            <p className="mt-0.5 text-xs text-slate-400">Takes under a minute. Useful before a risky change.</p>
          </div>
          <button type="button" onClick={handleRunNow} disabled={running}
            className="rounded-xl bg-[#1b2857] hover:bg-[#243469] disabled:opacity-60 text-white px-4 py-2.5 text-sm font-semibold whitespace-nowrap">
            {running ? 'Running…' : 'Run backup now'}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-800">Backup history</h2>
        </div>
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
        ) : backups.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400">No backups yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">File</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Created</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Size</th>
                <th className="px-4 py-2.5 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {backups.map((b) => (
                <tr key={b.name}>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{b.name}</td>
                  <td className="px-4 py-2.5 text-slate-500">{b.created_at}</td>
                  <td className="px-4 py-2.5 text-slate-500">{formatBytes(b.size_bytes)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => handleDownload(b.name)} className="text-xs font-semibold text-orange-600 hover:text-orange-700">
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
